import { coachingFeedbackQueries } from '@cobliteam/api/coachingFeedback';
import { CameraSafetyRequester, type Driver } from '@cobliteam/api-lib';

import type { Media, MediaView, PageableContent, SafetyOccurrence, SafetyReview, SafetyReviewHistory, SafetyReviewRequest, UUID } from '@cobliteam/common-types';
import Environment from '@cobliteam/react-environments';
import { getMediaStatusBasedOnReviewStatus, MediaTableType } from '@cobliteam/ui-business/utils/mediaUtils';
import { type DefinedInitialDataInfiniteOptions, type InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const ROOT_KEY = 'CAMERA-SAFETY';
const OCCURRENCES_KEY = [ROOT_KEY, 'OCCURRENCES'];
const REVIEW_HISTORY_KEY = [ROOT_KEY, 'REVIEW-HISTORY'];

const createOptimisticMediaWithSafetyReview = (media: Media, safetyReview: SafetyReview, updatedByName: string, driver?: Pick<Driver, 'id' | 'name'>): Media => {
  const unreviewedDriver = media.unreviewedDriver || media.driver;

  return {
    ...media,
    mediaStatus: getMediaStatusBasedOnReviewStatus(safetyReview.status),
    driver,
    unreviewedDriver,
    severity: safetyReview.severity || media.severity,
    safetyReview: {
      ...media.safetyReview,
      id: safetyReview.id,
      fleetId: safetyReview.fleetId,
      createdAt: safetyReview.createdAt,
      updatedAt: safetyReview.updatedAt,
      updatedByUser: {
        fullName: updatedByName,
      },
      severity: safetyReview.severity,
      originalMediaSeverity: safetyReview.originalMediaSeverity,
      createdBy: safetyReview.createdBy,
      updatedBy: safetyReview.updatedBy,
      status: safetyReview.status,
      driverId: safetyReview.driverId,
      dismissReason: safetyReview.dismissReason,
      driver,
      occurrences:
        safetyReview.occurrences?.map((occurrence) => {
          return {
            ...occurrence,
            id: occurrence.id,
            name: occurrence.name,
          };
        }) || [],
    },
  } satisfies Media;
};

const useCameraSafety = () => {
  const env = Environment.getInstance().getEnvironment();
  const cameraSafetyRequester = new CameraSafetyRequester({
    host: env.apiHost,
  });

  const getCameraSafetyOccurrences = () => {
    return useQuery<SafetyOccurrence[]>({
      queryKey: OCCURRENCES_KEY,
      queryFn: cameraSafetyRequester.getCameraSafetyOccurrences,
      staleTime: 300_000, // 5 minutes
    });
  };

  const upsertCameraSafetyReview = (page: MediaTableType) => {
    const queryClient = useQueryClient();

    return useMutation({
      mutationFn: (safetyReviewRequest: SafetyReviewRequest) => cameraSafetyRequester.upsertCameraSafetyReview(safetyReviewRequest),
      onSuccess: async (result, { mediaId, driver, updatedByName }) => {
        const queryKeyPage = page === MediaTableType.EVENT_MEDIA ? 'LIST-EVENT' : 'LIST-REQUEST';
        const bidirectionQueryKey = page === MediaTableType.EVENT_MEDIA ? 'BIDIRECTION-EVENTS' : 'BIDIRECTION-REQUESTS';

        if (mediaId) {
          // Invalidate cached history
          queryClient.removeQueries({ queryKey: [...REVIEW_HISTORY_KEY, mediaId] });

          await queryClient.invalidateQueries({
            queryKey: ['CAMERA-MEDIA', 'EVENTS-SEVERITY-SUMMARY'],
            refetchType: 'all',
          });

          // Coaching feedbacks may be created asynchronously by the backend as a side-effect
          // of the review, so their cache must be invalidated here.
          await queryClient.invalidateQueries({ queryKey: coachingFeedbackQueries.all() });

          // Invalidate cached inactive media lists. With changes on review, they may become invalid for the current filters
          await queryClient.invalidateQueries({
            queryKey: ['CAMERA-MEDIA', queryKeyPage],
            type: 'inactive',
          });

          // Optimistic updates for media details on "playlist"
          queryClient.setQueriesData(
            {
              queryKey: ['CAMERA-MEDIA', queryKeyPage, 'DETAILS-LIST'],
            },
            (old: InfiniteData<PageableContent<Media>> | undefined) => {
              if (!old) return old;

              const updatedPages = old.pages.map((item) => {
                return {
                  ...item,
                  content: item.content.map((media: Media) => {
                    if (media.id === mediaId) {
                      return createOptimisticMediaWithSafetyReview(media, result, updatedByName, driver);
                    }

                    return media;
                  }),
                };
              });

              return {
                ...old,
                pages: updatedPages,
              };
            }
          );

          queryClient.setQueriesData(
            {
              queryKey: ['CAMERA-MEDIA', bidirectionQueryKey],
            },
            (old: InfiniteData<PageableContent<Media>> | undefined) => {
              if (!old) return old;

              const updatedPages = old.pages.map((item) => {
                return {
                  ...item,
                  content: item.content.map((media: Media) => {
                    if (media.id === mediaId) {
                      return createOptimisticMediaWithSafetyReview(media, result, updatedByName, driver);
                    }

                    return media;
                  }),
                };
              });

              return {
                ...old,
                pages: updatedPages,
              };
            }
          );

          queryClient.setQueriesData(
            {
              queryKey: ['CAMERA-MEDIA', 'VIEW', mediaId],
            },
            (old: MediaView | undefined) => {
              if (!old) return old;

              return {
                ...old,
                mediaInfo: createOptimisticMediaWithSafetyReview(old.mediaInfo, result, updatedByName, driver),
              };
            }
          );

          queryClient.setQueriesData(
            {
              queryKey: ['MEDIA-RECOMMENDATIONS', 'DAILY'],
            },
            (old: PageableContent<Media> | undefined) => {
              if (!old) return old;

              const updatedContent = old.content.map((media: Media) => {
                if (media.id === mediaId) {
                  return createOptimisticMediaWithSafetyReview(media, result, updatedByName, driver);
                }
                return media;
              });

              return {
                ...old,
                content: updatedContent,
              };
            }
          );
        }
      },
    });
  };

  const getSafetyReviewHistory = (mediaId: UUID, options?: Partial<DefinedInitialDataInfiniteOptions<PageableContent<SafetyReviewHistory>>>) => {
    return useInfiniteQuery<PageableContent<SafetyReviewHistory>>({
      queryKey: [...REVIEW_HISTORY_KEY, mediaId],
      initialPageParam: 0,
      queryFn: ({ pageParam }) =>
        cameraSafetyRequester.getSafetyReviewHistory({
          mediaId,
          page: pageParam as number,
          size: 10,
          sort: ['createdAt,desc'],
        }),
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.last === false) return pages.length;
        return undefined;
      },
      staleTime: 60_000,
      ...options,
    });
  };

  return {
    getCameraSafetyOccurrences,
    upsertCameraSafetyReview,
    getSafetyReviewHistory,
  };
};

export default useCameraSafety;
